import { type FC, forwardRef, type HTMLAttributes } from 'react'
import { IMaskMixin } from 'react-imask'
import { type ReactMaskProps } from 'react-imask/dist/mixin'
import { TextField, type TextFieldProps } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { type AnyMaskedOptions } from 'imask'

import FormLabel from '@Components/utils/formLabel/FormLabel'
import InfoMessage from '@DS/components/components/informationMessages/infoMessage/InfoMessage'
import { type DescriptionMessage } from '@DS/components/components/tooltip/additionalMessage/AdditionalMessage.types'
import ClearButton from '@DS/components/forms/input/ClearButton'
import * as styles from '@DS/components/forms/input/styles'
import useUuid from '@Hooks/useUuid'

import { type BaseInputProps } from '../BaseInput.types'
import CalendarButton, { type CalendarButtonProps } from '../InputDate/_internal/CalendarButton'

/*
 * TODO IVTS-21796
 * En attendant un correctif pour
 * - https://github.com/uNmAnNeR/imaskjs/issues/639
 * - https://github.com/uNmAnNeR/imaskjs/issues/654
 */

type MaskedTextFieldProps = TextFieldProps & Partial<AnyMaskedOptions> & Partial<Omit<ReactMaskProps, 'inputRef'>>

const MaskedTextField = IMaskMixin((props: MaskedTextFieldProps) => (
  <TextField {...props} />
)) as FC<MaskedTextFieldProps>

type BaseMaskedInputProps = BaseInputProps & {
  blocks: Record<string, AnyMaskedOptions>
  calendarIcon?: CalendarButtonProps
  descriptionMessage?: DescriptionMessage
  onFormat?: (date: Date) => string
  parse?: (str: string) => Date
  pattern?: string
  isEndAdornmentHidden?: boolean
}

const BaseMaskedInput: FC<BaseMaskedInputProps> = forwardRef<unknown, BaseMaskedInputProps>(
  (
    {
      isError = false,
      isEndAdornmentHidden = false,
      name,
      blocks,
      value = '',
      parse,
      pattern,
      readOnly = false,
      buttonAriaLabel,
      onClear,
      onChange,
      onFormat,
      isRequired,
      helperText,
      calendarIcon,
      slotProps = {},
      ...rest
    },
    ref,
  ) => {
    const id = useUuid()
    const theme = useTheme()

    const disabledClassNameProps = readOnly ? { className: 'Mui-disabled' } : undefined

    const getRightIcon = () => {
      if (isEndAdornmentHidden) {
        return undefined
      }

      if (calendarIcon) {
        const { ariaLabel, name: calendarIconName, onClick, disabled } = calendarIcon

        return (
          <CalendarButton
            ariaLabel={ariaLabel || buttonAriaLabel}
            name={calendarIconName}
            onClick={onClick}
            disabled={disabled}
          />
        )
      }

      if (!rest.disabled && !readOnly && value && onClear) {
        return <ClearButton ariaLabel={buttonAriaLabel} name={name} onClear={onClear} />
      }

      return undefined
    }

    return (
      <div css={styles.inputWrapper}>
        {
          // TODO IVTS-21796
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          <MaskedTextField
            {...rest}
            {...disabledClassNameProps}
            css={styles.input(theme, isError, !!value, rest.disabled)}
            id={name ? `input-${name}` : (rest.id ?? `${id}-input-${name}`)}
            blocks={blocks}
            error={isError}
            format={onFormat}
            slotProps={{
              formHelperText: {
                ...disabledClassNameProps,
                'data-test': `${name}-input-text-helper`,
                component: 'div',
              } as HTMLAttributes<HTMLDivElement>,
              inputLabel: disabledClassNameProps,
              htmlInput: {
                ...slotProps.htmlInput,
                'aria-required': isRequired,
              },
              input: {
                ...disabledClassNameProps,
                endAdornment: getRightIcon(),
                readOnly,
                value,
              },
            }}
            helperText={
              isError && !!helperText && <InfoMessage description={helperText} severity="alert" isAriaLabelDisabled />
            }
            parse={parse}
            name={name}
            type="tel"
            value={value}
            variant="outlined"
            onAccept={onChange}
            onComplete={onChange}
            mask={pattern}
            inputRef={ref}
            label={<FormLabel label={rest.label} isRequired={isRequired} />}
          />
        }
      </div>
    )
  },
)

export default BaseMaskedInput
